import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  FaGlobe, FaFacebook, FaInstagram, FaTwitter, FaMapMarkerAlt, 
  FaChevronDown, FaChevronUp, FaEnvelope, FaSort, FaSortUp, FaSortDown,
  FaTimes, FaInfoCircle, FaTasks, FaGoogle, FaStickyNote, FaFilter, 
  FaHome, FaEnvelopeOpen, FaFileImport, FaCog
} from 'react-icons/fa';
import axios from 'axios';
import config from '../config';

interface Lead {
  id: number;
  name: string;
  website?: string | null;
  facebook?: string | null;
  instagram?: string | null;
  twitter?: string | null;
  address?: string | null;
  categories?: string | null;
  email?: string | null;
  average_rating?: number | null;
  review_count?: number | null;
  status?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  distance_from_so16?: number | null;
  notes?: string | null;
  featured_image?: string | null;
  google_knowledge_url?: string | null;
  opening_hours?: string | null;
}

interface LeadsApiResponse {
  records: Lead[];
}

const ITEMS_PER_PAGE = 100;

type SortableLeadKey = 'name' | 'categories' | 'average_rating' | 'review_count' | 'distance_from_so16' | 'status';

interface SortConfig {
  key: SortableLeadKey | null;
  direction: 'ascending' | 'descending';
}

const LEAD_STATUSES = [
    'new', 'email_sent', 'contacted_in_person', 'waiting', 
    'followed_up', 'inside_trial', 'subscribed', 'not_interested'
];

// --- Utility Function for Decoding HTML Entities ---
function decodeHtmlEntities(text: string): string {
  if (typeof document === 'undefined') { 
    // Basic server-side fallback or if document isn't available
    return text.replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#039;/g, "'");
  } 
  const textArea = document.createElement('textarea');
  textArea.innerHTML = text;
  return textArea.value;
}

// Add a helper to truncate text for the tooltip
function truncateText(text: string | null | undefined, maxLength: number): string {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
}

// --- Status Color Mapping ---
const statusColorMap: { [key: string]: string } = {
  new: 'bg-blue-100 text-blue-800',
  email_sent: 'bg-purple-100 text-purple-800',
  contacted_in_person: 'bg-indigo-100 text-indigo-800',
  waiting: 'bg-yellow-100 text-yellow-800',
  followed_up: 'bg-teal-100 text-teal-800',
  inside_trial: 'bg-cyan-100 text-cyan-800',
  subscribed: 'bg-green-100 text-green-800',
  not_interested: 'bg-gray-100 text-gray-700',
  default: 'bg-gray-100 text-gray-700' // Fallback
};

const Dashboard: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [socialFilters, setSocialFilters] = useState({
    hasEmail: false,
    hasWebsite: false,
    hasFacebook: false,
    hasInstagram: false,
    hasNotes: false
  });
  const [selectedCategories, setSelectedCategories] = useState<Set<string>>(new Set());
  const [categoryFilterLogic, setCategoryFilterLogic] = useState<'OR' | 'AND' | 'EXCLUDE'>('OR');
  const [selectedStatuses, setSelectedStatuses] = useState<Set<string>>(new Set());
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(true);
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(true);
  const [isSocialsDropdownOpen, setIsSocialsDropdownOpen] = useState(true);
  const [isRatingReviewDropdownOpen, setIsRatingReviewDropdownOpen] = useState(true);
  const [isDistanceDropdownOpen, setIsDistanceDropdownOpen] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [ratingRange, setRatingRange] = useState<[number, number]>([0, 5]);
  const [reviewRange, setReviewRange] = useState<[number, number]>([0, 1000]);
  const [maxReviewCount, setMaxReviewCount] = useState<number>(1000);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: null, direction: 'ascending' });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [activeTab, setActiveTab] = useState<'info' | 'manage'>('info');
  const [currentLeadStatus, setCurrentLeadStatus] = useState<string>('');
  const [currentLeadNotes, setCurrentLeadNotes] = useState<string>('');
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const [shouldFocusNotes, setShouldFocusNotes] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [distanceRange, setDistanceRange] = useState<number>(200);
  const [filterByDistance, setFilterByDistance] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const queryClient = useQueryClient();

  const { data: apiResponse, isLoading, error } = useQuery<LeadsApiResponse>({
    queryKey: ['leads'],
    queryFn: async () => {
      const response = await axios.get(`${config.apiBaseUrl}/leads/read.php`);
      if (!response.data || !Array.isArray(response.data.records)) {
        console.warn('API response did not contain a records array:', response.data);
        return { records: [] }; 
      }
      let currentMaxReviews = 0;
      const cleanedRecords = response.data.records.map((lead: any) => {
        const reviewCount = lead.review_count != null ? Number(lead.review_count) : 0;
        if (reviewCount > currentMaxReviews) {
            currentMaxReviews = reviewCount;
        }
        return {
          ...lead,
          categories: lead.categories || '',
          distance_from_so16: lead.distance_from_so16 != null ? Number(lead.distance_from_so16) : null,
          average_rating: lead.average_rating != null ? Number(lead.average_rating) : null,
          review_count: reviewCount,
          email: lead.email || null,
          status: lead.status || null,
          featured_image: lead.featured_image || null,
          google_knowledge_url: lead.google_knowledge_url || null,
          opening_hours: lead.opening_hours || null,
          address: lead.address || null,
          website: lead.website || null,
          facebook: lead.facebook || null,
          instagram: lead.instagram || null,
          twitter: lead.twitter || null,
          notes: lead.notes || null,
        };
      });
      if (currentMaxReviews > 0) {
          setMaxReviewCount(currentMaxReviews);
          setReviewRange(prev => [prev[0], Math.max(prev[1], currentMaxReviews)]);
      }
      return { records: cleanedRecords };
    },
  });

  const allLeads = useMemo(() => apiResponse?.records || [], [apiResponse]);

  const uniqueCategories = useMemo(() => {
    const categoriesSet = new Set<string>();
    allLeads.forEach(lead => {
      if (lead.categories) {
        lead.categories.split(',').forEach(cat => {
          const trimmedCat = cat.trim();
          if (trimmedCat) {
            categoriesSet.add(trimmedCat);
          }
        });
      }
    });
    return Array.from(categoriesSet).sort();
  }, [allLeads]);

  const filteredLeads = useMemo(() => {
    return allLeads.filter((lead) => {
      const matchesSearch = !searchTerm || lead.name.toLowerCase().includes(searchTerm.toLowerCase());
      if (!matchesSearch) return false;

      const matchesSocialFilters = Object.entries(socialFilters).every(([key, value]) => {
        if (!value) return true;
        switch (key) {
          case 'hasEmail': return !!lead.email;
          case 'hasWebsite': return !!lead.website;
          case 'hasFacebook': return !!lead.facebook;
          case 'hasInstagram': return !!lead.instagram;
          case 'hasNotes': return !!lead.notes && lead.notes.trim() !== '';
          default: return true;
        }
      });
      if (!matchesSocialFilters) return false;
      
      if (selectedCategories.size > 0) {
        const leadCategories = new Set(lead.categories?.split(',').map(c => c.trim()).filter(Boolean) || []);
        
        if (categoryFilterLogic === 'AND') {
          const hasAll = Array.from(selectedCategories).every(cat => leadCategories.has(cat));
          if (!hasAll) return false;
        } else if (categoryFilterLogic === 'OR') {
          const hasAny = Array.from(selectedCategories).some(cat => leadCategories.has(cat));
           if (!hasAny) return false;
        } else if (categoryFilterLogic === 'EXCLUDE') {
          const hasAnyExcluded = Array.from(selectedCategories).some(cat => leadCategories.has(cat));
          if (hasAnyExcluded) return false;
        }
      }

      // Distance filter
      if (filterByDistance) {
        if (lead.distance_from_so16 === null || lead.distance_from_so16 === undefined) {
          return false;
        }
        if (lead.distance_from_so16 > distanceRange) {
          return false;
        }
      }

      const leadRating = lead.average_rating;
      if (leadRating === null || leadRating === undefined) {
          if (ratingRange[0] > 0 || ratingRange[1] < 5) return false; 
      } else if (leadRating < ratingRange[0] || leadRating > ratingRange[1]) {
          return false;
      }

      const leadReviews = lead.review_count;
      if (leadReviews === null || leadReviews === undefined) {
         if (reviewRange[0] > 0) return false;
      } else if (leadReviews < reviewRange[0] || leadReviews > reviewRange[1]) {
          return false;
      }

      if (selectedStatuses.size > 0) {
          if (!lead.status || !selectedStatuses.has(lead.status)) {
              return false;
          }
      }

      return true;
    });
  }, [allLeads, searchTerm, socialFilters, selectedCategories, categoryFilterLogic, ratingRange, reviewRange, selectedStatuses, filterByDistance, distanceRange]);
  
  const sortedLeads = useMemo(() => {
    let sortableItems = [...filteredLeads];
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        const aValue = a[sortConfig.key!];
        const bValue = b[sortConfig.key!];

        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return sortConfig.direction === 'ascending' ? 1 : -1;
        if (bValue == null) return sortConfig.direction === 'ascending' ? -1 : 1;

        const compareA = typeof aValue === 'string' ? aValue.toLowerCase() : aValue;
        const compareB = typeof bValue === 'string' ? bValue.toLowerCase() : bValue;

        if (compareA < compareB) return sortConfig.direction === 'ascending' ? -1 : 1;
        if (compareA > compareB) return sortConfig.direction === 'ascending' ? 1 : -1;
        return 0;
      });
    }
    return sortableItems;
  }, [filteredLeads, sortConfig]);

  const totalPages = Math.ceil(sortedLeads.length / ITEMS_PER_PAGE);
  const paginatedLeads = useMemo(() => {
    const itemsToPaginate = sortedLeads;
    if (itemsToPaginate.length <= ITEMS_PER_PAGE) {
        return itemsToPaginate;
    }
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return itemsToPaginate.slice(startIndex, endIndex);
  }, [sortedLeads, currentPage]);

   useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, socialFilters, selectedCategories, categoryFilterLogic, ratingRange, reviewRange, sortConfig, selectedStatuses, filterByDistance, distanceRange]);

   useEffect(() => {
      if (isModalOpen && activeTab === 'manage' && shouldFocusNotes) {
          const timer = setTimeout(() => {
             const textarea = document.getElementById('leadNotesTextarea');
             textarea?.focus();
             setShouldFocusNotes(false);
          }, 100);
          
          return () => clearTimeout(timer);
      }
   }, [isModalOpen, activeTab, shouldFocusNotes]);

  const handleCategoryChange = (category: string) => {
    setSelectedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(category)) {
        newSet.delete(category);
      } else {
        newSet.add(category);
      }
      return newSet;
    });
  };
  
  const handleSocialFilterChange = (filterName: keyof typeof socialFilters) => {
     setSocialFilters(prev => ({
       ...prev,
       [filterName]: !prev[filterName]
     }));
  };
  
  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleRatingChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(event.target.value);
    const index = parseInt(event.target.name, 10);
    setRatingRange(prev => {
       const newState = [...prev] as [number, number];
       newState[index] = value;
       if (newState[0] > newState[1]) {
          if (index === 0) newState[1] = newState[0];
          else newState[0] = newState[1];
       }
       return newState;
    });
  };

  const handleReviewChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value, 10);
    const index = parseInt(event.target.name, 10);
    setReviewRange(prev => {
      const newState = [...prev] as [number, number];
      newState[index] = isNaN(value) ? 0 : value;
       if (newState[0] > newState[1]) {
          if (index === 0) newState[1] = newState[0];
          else newState[0] = newState[1];
       }
      newState[0] = Math.max(0, newState[0]);
      newState[1] = Math.min(maxReviewCount, newState[1]);
      return newState;
    });
  };

  const requestSort = (key: SortableLeadKey) => {
    let direction: 'ascending' | 'descending' = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnKey: SortableLeadKey) => {
    if (sortConfig.key !== columnKey) {
      return <FaSort className="ml-1 h-3 w-3 text-gray-400" />;
    }
    if (sortConfig.direction === 'ascending') {
      return <FaSortUp className="ml-1 h-3 w-3 text-gray-600" />;
    }
    return <FaSortDown className="ml-1 h-3 w-3 text-gray-600" />;
  };

  const openModal = (lead: Lead, startTab: 'info' | 'manage' = 'info', focusNotes: boolean = false) => {
    setSelectedLead(lead);
    setActiveTab(startTab);
    setCurrentLeadStatus(lead.status || 'new');
    setCurrentLeadNotes(lead.notes || '');
    setShouldFocusNotes(focusNotes);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedLead(null);
    setCurrentLeadStatus('');
    setCurrentLeadNotes('');
    setShouldFocusNotes(false);
  };

  const handleStatusUpdate = async () => {
    if (!selectedLead) return;
    setIsUpdatingStatus(true);
    try { 
      const response = await axios.post(`${config.apiBaseUrl}/leads/update_status.php`, { 
          leadId: selectedLead.id, 
          status: currentLeadStatus 
      });
      if (response.data.success) {
          queryClient.invalidateQueries({ queryKey: ['leads'] });
          setSelectedLead(prev => prev ? { ...prev, status: currentLeadStatus } : null);
      } else {
           alert(`Error updating status: ${response.data.message || 'Unknown error'}`);
      }
    } catch (err) { 
        console.error("Status update API error:", err);
        alert(`Failed to send status update request: ${(err as Error).message}`);
    } finally { 
        setIsUpdatingStatus(false); 
    }
  };

  const handleNotesSave = async () => {
    if (!selectedLead) return;
    setIsSavingNotes(true);
     try { 
      const response = await axios.post(`${config.apiBaseUrl}/leads/update_notes.php`, { 
          leadId: selectedLead.id, 
          notes: currentLeadNotes
      });
       if (response.data.success) {
          queryClient.invalidateQueries({ queryKey: ['leads'] });
          setSelectedLead(prev => prev ? { ...prev, notes: currentLeadNotes } : null);
      } else {
           alert(`Error saving notes: ${response.data.message || 'Unknown error'}`);
      }
    } catch (err) { 
        console.error("Notes save API error:", err);
        alert(`Failed to send notes save request: ${(err as Error).message}`);
    } finally { 
        setIsSavingNotes(false); 
    }
  };

  const handleSendEmailClick = () => {
      if (!selectedLead) return;
      console.log(`TODO: Initiate email sending for lead ${selectedLead.id} (Email: ${selectedLead.email})`);
      alert('Email sending functionality needs implementation (e.g., open new modal/page).');
  };

  const handleStatusToggle = (status: string) => {
      setSelectedStatuses(prev => {
          const newSet = new Set(prev);
          if (newSet.has(status)) {
              newSet.delete(status);
          } else {
              newSet.add(status);
          }
          return newSet;
      });
  };

  // Add window resize handler
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Determine which columns to show based on window width
  const visibleColumns = useMemo(() => {
    // Order columns by priority: business (always), status, category, links, rating, reviews, distance
    if (windowWidth >= 1280) return ['all']; // Show all columns
    if (windowWidth >= 1024) return ['business', 'category', 'status', 'links', 'reviews', 'rating']; // Drop distance
    if (windowWidth >= 768) return ['business', 'category', 'status', 'links']; // Drop reviews, rating
    if (windowWidth >= 640) return ['business', 'category', 'status']; // Drop links
    return ['business', 'status']; // Minimum columns for mobile
  }, [windowWidth]);

  // Add delete handler function after handleNotesSave
  const handleDeleteLead = async () => {
    if (!selectedLead) return;
    setIsDeleting(true);
    try {
      const response = await axios.post(`${config.apiBaseUrl}/leads/delete.php`, { 
        leadId: selectedLead.id
      });
      
      if (response.data.success) {
        queryClient.invalidateQueries({ queryKey: ['leads'] });
        closeModal();
        // Optional: show success message
      } else {
        alert(`Error deleting lead: ${response.data.message || 'Unknown error'}`);
      }
    } catch (err) {
      console.error("Lead delete API error:", err);
      alert(`Failed to delete lead: ${(err as Error).message}`);
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  if (isLoading) return <div className="p-6">Loading leads...</div>;
  if (error) return <div className="p-6 text-red-600">Error loading leads: {(error as Error).message}</div>;

  return (
    <>
      {/* Main content - works with the existing layout structure */}
      <main className="py-6 w-full">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex min-h-screen">
            {/* Main content area */}
            <div className="w-full flex-1 overflow-hidden">
              {/* Header */}
              <div className="flex justify-between items-center bg-white p-4 rounded-lg shadow mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Leads Dashboard</h2>
                <div className="flex items-center">
                  <div className="text-sm font-semibold px-3 py-1.5 bg-primary-50 text-primary-700 rounded-full border border-primary-200">
                    Total Leads: {isLoading ? "Loading..." : allLeads.length.toLocaleString()}
                  </div>
                </div>
              </div>

              {/* Search Bar */}
              <div className="bg-white p-4 rounded-lg shadow mb-6">
                <input 
                  type="text" 
                  placeholder="Search leads by name..." 
                  className="px-4 py-2 border rounded-md w-full" 
                  value={searchTerm} 
                  onChange={(e) => setSearchTerm(e.target.value)} 
                />
              </div>

              {/* Table with proper responsive behavior */}
              <div className="bg-white shadow rounded-lg w-full">
                <div className="w-full overflow-x-auto">
                  <table className="w-full divide-y divide-gray-200 table-fixed">
                    <thead className="bg-gray-50">
                      <tr>
                        {/* Business Column - Always visible, takes more space on larger screens */}
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 w-[30%]" onClick={() => requestSort('name')}>
                          <div className="flex items-center">Business {getSortIcon('name')}</div>
                        </th>

                        {/* Category Column */}
                        {(visibleColumns.includes('all') || visibleColumns.includes('category')) && (
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 w-[15%]" onClick={() => requestSort('categories')}>
                            <div className="flex items-center">Category {getSortIcon('categories')}</div>
                          </th>
                        )}

                        {/* Rating Column */}
                        {(visibleColumns.includes('all') || visibleColumns.includes('rating')) && (
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 w-[10%]" onClick={() => requestSort('average_rating')}>
                            <div className="flex items-center">Rating {getSortIcon('average_rating')}</div>
                          </th>
                        )}

                        {/* Reviews Column */}
                        {(visibleColumns.includes('all') || visibleColumns.includes('reviews')) && (
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 w-[10%]" onClick={() => requestSort('review_count')}>
                            <div className="flex items-center">Reviews {getSortIcon('review_count')}</div>
                          </th>
                        )}

                        {/* Distance Column */}
                        {(visibleColumns.includes('all') || visibleColumns.includes('distance')) && (
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 w-[10%]" onClick={() => requestSort('distance_from_so16')}>
                            <div className="flex items-center">Distance {getSortIcon('distance_from_so16')}</div>
                          </th>
                        )}

                        {/* Status Column */}
                        {(visibleColumns.includes('all') || visibleColumns.includes('status')) && (
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 w-[10%]" onClick={() => requestSort('status')}>
                            <div className="flex items-center">Status {getSortIcon('status')}</div>
                          </th>
                        )}

                        {/* Links Column */}
                        {(visibleColumns.includes('all') || visibleColumns.includes('links')) && (
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[15%]">Links</th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {paginatedLeads.length > 0 ? paginatedLeads.map((lead) => {
                        const statusClass = statusColorMap[lead.status || 'default'] || statusColorMap.default;
                        const googleMapsOrigin = lead.address ? encodeURIComponent(lead.address) : (lead.latitude && lead.longitude ? `${lead.latitude},${lead.longitude}` : '');
                        const googleMapsDirectionsUrl = googleMapsOrigin ? `https://www.google.com/maps/dir/?api=1&origin=${googleMapsOrigin}&destination=SO16+5SQ` : '#';
                        
                        return (
                          <tr key={lead.id} className="hover:bg-gray-50">
                            {/* Business Column - Always visible */}
                            <td className="px-4 py-3 text-sm font-medium text-primary-600 hover:text-primary-800 truncate cursor-pointer" onClick={() => openModal(lead, 'info', false)}>
                              {decodeHtmlEntities(lead.name)}
                            </td>

                            {/* Category Column */}
                            {(visibleColumns.includes('all') || visibleColumns.includes('category')) && (
                              <td className="px-4 py-3 text-sm text-gray-500 truncate">
                                <div 
                                  title={lead.categories ? lead.categories.split(',').map(category => category.trim()).join(', ') : 'N/A'}
                                >
                                  {lead.categories?.split(',')[0].trim() || 'N/A'}
                                </div>
                              </td>
                            )}

                            {/* Rating Column */}
                            {(visibleColumns.includes('all') || visibleColumns.includes('rating')) && (
                              <td className="px-4 py-3 text-sm text-gray-500">
                                {lead.average_rating != null ? `${lead.average_rating.toFixed(1)} ★` : 'N/A'}
                              </td>
                            )}

                            {/* Reviews Column */}
                            {(visibleColumns.includes('all') || visibleColumns.includes('reviews')) && (
                              <td className="px-4 py-3 text-sm text-gray-500">
                                {lead.review_count ?? 'N/A'}
                              </td>
                            )}

                            {/* Distance Column */}
                            {(visibleColumns.includes('all') || visibleColumns.includes('distance')) && (
                              <td className="px-4 py-3 text-sm text-gray-500">
                                {lead.distance_from_so16 != null || googleMapsOrigin ? (
                                  <a 
                                    href={googleMapsDirectionsUrl} 
                                    target="_blank" 
                                    rel="noopener noreferrer" 
                                    className="inline-flex items-center gap-1 hover:text-blue-600 hover:underline group truncate"
                                    title={lead.address ? decodeHtmlEntities(lead.address) : 'View directions on Google Maps'}
                                    onClick={(e) => {!googleMapsOrigin && e.preventDefault()}}
                                  >
                                    <FaMapMarkerAlt className="h-4 w-4 text-gray-400 group-hover:text-red-500 flex-shrink-0 transition-colors duration-150" />
                                    <span className="truncate">
                                      {lead.distance_from_so16 != null ? `${lead.distance_from_so16.toFixed(2)} km` : 'Directions'}
                                    </span>
                                  </a>
                                ) : (
                                  <span>N/A</span>
                                )}
                              </td>
                            )}

                            {/* Status Column */}
                            {(visibleColumns.includes('all') || visibleColumns.includes('status')) && (
                              <td className="px-4 py-3">
                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full truncate ${statusClass}`}>
                                  {lead.status ? lead.status.replace('_', ' ') : 'N/A'}
                                </span>
                              </td>
                            )}

                            {/* Links Column */}
                            {(visibleColumns.includes('all') || visibleColumns.includes('links')) && (
                              <td className="px-4 py-3 text-sm font-medium">
                                <div className="flex space-x-2 items-center">
                                  <div className="group relative inline-flex items-center">
                                     <button 
                                       onClick={(e) => {
                                         e.stopPropagation();
                                         openModal(lead, 'manage', true);
                                       }}
                                       className={`text-gray-400 hover:text-yellow-600 ${!(lead.notes && lead.notes.trim() !== '') ? 'opacity-20' : ''}`}
                                       title={lead.notes && lead.notes.trim() !== '' ? "View/Edit Notes" : "Add Notes"}
                                     >
                                         <FaStickyNote className="h-5 w-5" />
                                     </button>
                                  </div>
                                  {lead.email && <a 
                                    href={`mailto:${lead.email.split(',')[0].trim()}`}
                                    title={`Email ${lead.email.split(',')[0].trim()}`}
                                    className="text-gray-400 hover:text-gray-600"
                                  ><FaEnvelope className="h-5 w-5" /></a>}
                                  {lead.website && <a href={lead.website} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-primary-500" title={lead.website}><FaGlobe className="h-5 w-5" /></a>}
                                  {lead.facebook && <a href={lead.facebook} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-blue-600" title={lead.facebook}><FaFacebook className="h-5 w-5" /></a>}
                                  {lead.instagram && <a href={lead.instagram} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-pink-500" title={lead.instagram}><FaInstagram className="h-5 w-5" /></a>}
                                </div>
                              </td>
                            )}
                          </tr>
                        );
                      }) : (
                        <tr>
                          <td colSpan={visibleColumns.includes('all') ? 7 : 1 + (visibleColumns.filter(col => col !== 'business').length)} className="text-center py-10 text-gray-500">
                            No leads match the current filters.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                {sortedLeads.length > ITEMS_PER_PAGE && (
                  <div className="px-6 py-3 bg-white border-t border-gray-200 flex items-center justify-between">
                    <div className="text-sm text-gray-700">
                      Showing <span className="font-medium">{(currentPage - 1) * ITEMS_PER_PAGE + 1}</span> to <span className="font-medium">{Math.min(currentPage * ITEMS_PER_PAGE, sortedLeads.length)}</span> of <span className="font-medium">{sortedLeads.length}</span> results
                    </div>
                    <div className="flex items-center space-x-1">
                      {/* First page */}
                      <button 
                        onClick={() => handlePageChange(1)} 
                        disabled={currentPage === 1} 
                        className="px-2 py-1 border border-gray-300 text-sm rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                        title="First page"
                      >
                        <span className="sr-only">First page</span>
                        |&lt;
                      </button>
                      
                      {/* Previous page */}
                      <button 
                        onClick={() => handlePageChange(currentPage - 1)} 
                        disabled={currentPage === 1} 
                        className="px-2 py-1 border border-gray-300 text-sm rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                        title="Previous page"
                      >
                        <span className="sr-only">Previous page</span>
                        &lt;
                      </button>
                      
                      {/* Page numbers */}
                      {(() => {
                        const pageNumbers = [];
                        let startPage = Math.max(1, currentPage - 2);
                        let endPage = Math.min(totalPages, startPage + 4);
                        
                        // Adjust start if we're near the end
                        if (endPage - startPage < 4) {
                          startPage = Math.max(1, endPage - 4);
                        }
                        
                        for (let i = startPage; i <= endPage; i++) {
                          pageNumbers.push(
                            <button
                              key={i}
                              onClick={() => handlePageChange(i)}
                              className={`px-3 py-1 border text-sm rounded-md ${
                                currentPage === i
                                  ? 'bg-primary-100 border-primary-500 text-primary-600 font-medium' 
                                  : 'border-gray-300 hover:bg-gray-50'
                              }`}
                            >
                              {i}
                            </button>
                          );
                        }
                        
                        return pageNumbers;
                      })()}
                      
                      {/* Next page */}
                      <button 
                        onClick={() => handlePageChange(currentPage + 1)} 
                        disabled={currentPage === totalPages} 
                        className="px-2 py-1 border border-gray-300 text-sm rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                        title="Next page"
                      >
                        <span className="sr-only">Next page</span>
                        &gt;
                      </button>
                      
                      {/* Last page */}
                      <button 
                        onClick={() => handlePageChange(totalPages)} 
                        disabled={currentPage === totalPages} 
                        className="px-2 py-1 border border-gray-300 text-sm rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                        title="Last page"
                      >
                        <span className="sr-only">Last page</span>
                        &gt;|
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          
            {/* Right Filters Sidebar - Fixed position */}
            <div className="w-full lg:w-[300px] lg:ml-4 mt-6 lg:mt-0">
              <div className="bg-white shadow rounded-lg p-4 lg:sticky lg:top-4">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Filters</h3>
                
                {/* Categories filter section */}
                <div className="mb-6">
                  <button onClick={() => setIsCategoryDropdownOpen(!isCategoryDropdownOpen)} className="flex justify-between items-center w-full text-left font-medium mb-2">
                    <span>Categories ({selectedCategories.size})</span>
                    {isCategoryDropdownOpen ? <FaChevronUp /> : <FaChevronDown />}
                  </button>
                  {isCategoryDropdownOpen && (
                    <div className="space-y-3 border-t pt-3 mt-2">
                      <div className="flex space-x-4 text-sm">
                        <label className="flex items-center"><input type="radio" name="catLogic" value="OR" checked={categoryFilterLogic === 'OR'} onChange={() => setCategoryFilterLogic('OR')} className="mr-1"/> Or</label>
                        <label className="flex items-center"><input type="radio" name="catLogic" value="AND" checked={categoryFilterLogic === 'AND'} onChange={() => setCategoryFilterLogic('AND')} className="mr-1"/> And</label>
                        <label className="flex items-center"><input type="radio" name="catLogic" value="EXCLUDE" checked={categoryFilterLogic === 'EXCLUDE'} onChange={() => setCategoryFilterLogic('EXCLUDE')} className="mr-1"/> Exclude</label>
                      </div>
                      <div className="max-h-60 overflow-y-auto overflow-x-hidden border rounded-md p-2 space-y-1">
                        {uniqueCategories.length > 0 ? uniqueCategories.map(category => (
                          <label key={category} className="flex items-center text-sm hover:bg-gray-50 p-1 rounded">
                            <input 
                              type="checkbox" 
                              checked={selectedCategories.has(category)} 
                              onChange={() => handleCategoryChange(category)}
                              className="mr-2 rounded text-primary-600"
                            />
                            <span className="truncate">{category}</span>
                          </label>
                        )) : <p className="text-xs text-gray-500 italic p-1">No categories found in current leads.</p>}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Status filter section */}
                <div className="mb-6">
                  <button onClick={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)} className="flex justify-between items-center w-full text-left font-medium mb-2">
                    <span>Status ({selectedStatuses.size})</span>
                    {isStatusDropdownOpen ? <FaChevronUp /> : <FaChevronDown />}
                  </button>
                  {isStatusDropdownOpen && (
                    <div className="space-y-2 border-t pt-3 mt-2">
                      <div className="flex flex-wrap gap-2">
                        {LEAD_STATUSES.map(status => {
                          const isSelected = selectedStatuses.has(status);
                          const baseClasses = statusColorMap[status] || statusColorMap.default;
                          const selectedClasses = isSelected ? 'border-2 border-gray-500 brightness-90' : 'border border-transparent';
                          
                          return (
                            <button
                              key={status}
                              onClick={() => handleStatusToggle(status)}
                              className={`px-2.5 py-1 text-xs font-medium rounded-full transition-all ${baseClasses} ${selectedClasses}`}
                            >
                              {status.replace('_', ' ')}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Socials & Contact filter section */}
                <div className="mb-6">
                  <button onClick={() => setIsSocialsDropdownOpen(!isSocialsDropdownOpen)} className="flex justify-between items-center w-full text-left font-medium mb-2">
                    <span>Socials & Contact</span>
                    {isSocialsDropdownOpen ? <FaChevronUp /> : <FaChevronDown />}
                  </button>
                  {isSocialsDropdownOpen && (
                    <div className="space-y-2 pl-2 border-t pt-3 mt-2">
                      <label className="flex items-center text-sm">
                        <input type="checkbox" checked={socialFilters.hasEmail} onChange={() => handleSocialFilterChange('hasEmail')} className="mr-2 rounded text-primary-600"/>
                        Has Email
                      </label>
                      <label className="flex items-center text-sm">
                        <input type="checkbox" checked={socialFilters.hasWebsite} onChange={() => handleSocialFilterChange('hasWebsite')} className="mr-2 rounded text-primary-600"/>
                        Has Website
                      </label>
                      <label className="flex items-center text-sm">
                        <input type="checkbox" checked={socialFilters.hasFacebook} onChange={() => handleSocialFilterChange('hasFacebook')} className="mr-2 rounded text-primary-600"/>
                        Has Facebook
                      </label>
                      <label className="flex items-center text-sm">
                        <input type="checkbox" checked={socialFilters.hasInstagram} onChange={() => handleSocialFilterChange('hasInstagram')} className="mr-2 rounded text-primary-600"/>
                        Has Instagram
                      </label>
                      <label className="flex items-center text-sm">
                        <input type="checkbox" checked={socialFilters.hasNotes} onChange={() => handleSocialFilterChange('hasNotes')} className="mr-2 rounded text-primary-600"/>
                        Has Notes
                      </label>
                    </div>
                  )}
                </div>
                
                {/* Rating & Reviews filter section */}
                <div>
                  <button onClick={() => setIsRatingReviewDropdownOpen(!isRatingReviewDropdownOpen)} className="flex justify-between items-center w-full text-left font-medium mb-2">
                    <span>Rating & Reviews</span>
                    {isRatingReviewDropdownOpen ? <FaChevronUp /> : <FaChevronDown />}
                  </button>
                  {isRatingReviewDropdownOpen && (
                    <div className="space-y-4 pl-2 text-sm border-t pt-3 mt-2">
                      <div>
                        <label htmlFor="minRating" className="block mb-1 font-medium">Min Rating: {ratingRange[0].toFixed(1)}</label>
                        <input type="range" id="minRating" name="0" min="0" max="5" step="0.1" value={ratingRange[0]} onChange={handleRatingChange} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"/>
                        <label htmlFor="maxRating" className="block mb-1 mt-2 font-medium">Max Rating: {ratingRange[1].toFixed(1)}</label>
                        <input type="range" id="maxRating" name="1" min="0" max="5" step="0.1" value={ratingRange[1]} onChange={handleRatingChange} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"/>
                      </div>
                      <div>
                        <label htmlFor="minReview" className="block mb-1 font-medium">Min Reviews: {reviewRange[0]}</label>
                        <input type="range" id="minReview" name="0" min="0" max={maxReviewCount} step="1" value={reviewRange[0]} onChange={handleReviewChange} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"/>
                        <label htmlFor="maxReview" className="block mb-1 mt-2 font-medium">Max Reviews: {reviewRange[1]}</label>
                        <input type="range" id="maxReview" name="1" min="0" max={maxReviewCount} step="1" value={reviewRange[1]} onChange={handleReviewChange} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"/>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Distance filter section */}
                <div className="mt-6">
                  <button onClick={() => setIsDistanceDropdownOpen(!isDistanceDropdownOpen)} className="flex justify-between items-center w-full text-left font-medium mb-2">
                    <span>Distance Filter</span>
                    {isDistanceDropdownOpen ? <FaChevronUp /> : <FaChevronDown />}
                  </button>
                  {isDistanceDropdownOpen && (
                    <div className="space-y-4 pl-2 text-sm border-t pt-3 mt-2">
                      <label className="flex items-center mb-3">
                        <input 
                          type="checkbox" 
                          checked={filterByDistance} 
                          onChange={() => setFilterByDistance(!filterByDistance)} 
                          className="mr-2 rounded text-primary-600"
                        />
                        Filter by distance
                      </label>
                      
                      <div className={filterByDistance ? 'opacity-100' : 'opacity-50 pointer-events-none'}>
                        <label htmlFor="distanceRange" className="block mb-1 font-medium">
                          Maximum Distance: {distanceRange} km
                        </label>
                        <input 
                          type="range" 
                          id="distanceRange" 
                          min="0" 
                          max="200" 
                          step="1" 
                          value={distanceRange} 
                          onChange={(e) => setDistanceRange(parseInt(e.target.value, 10))} 
                          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                        />
                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                          <span>0 km</span>
                          <span>100 km</span>
                          <span>200 km</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Lead details modal remains unchanged */}
      {isModalOpen && selectedLead && (
         <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
               <div className="flex justify-between items-center p-4 border-b">
                 <h2 className="text-xl font-semibold text-gray-800">{decodeHtmlEntities(selectedLead.name)}</h2>
                 <button onClick={closeModal} className="text-gray-400 hover:text-gray-600"><FaTimes className="h-5 w-5" /></button>
               </div>

               <div className="flex-grow overflow-y-auto p-6">
                 <div className="mb-6 border-b">
                     <nav className="-mb-px flex space-x-6">
                         <button onClick={() => setActiveTab('info')} className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'info' ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>
                           <FaInfoCircle className="inline-block mr-2 h-4 w-4"/> Lead Info
                         </button>
                         <button onClick={() => setActiveTab('manage')} className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'manage' ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>
                            <FaTasks className="inline-block mr-2 h-4 w-4"/> Manage Lead
                         </button>
                     </nav>
                 </div>

                 <div>
                     {activeTab === 'info' && (
                        <div className="space-y-4 text-sm">
                            <div className="flex flex-col md:flex-row gap-6 mb-4">
                               <div className="md:w-1/3 flex-shrink-0">
                                  {selectedLead.featured_image ? (
                                     <img 
                                        src={selectedLead.featured_image} 
                                        alt={`${selectedLead.name} featured`} 
                                        className="w-full h-auto rounded-md object-contain shadow bg-gray-100 max-h-60"
                                        onError={(e) => (e.currentTarget.style.display = 'none')}
                                     />
                                  ) : (
                                    <div className="w-full h-40 bg-gray-100 rounded-md flex items-center justify-center text-gray-400">
                                        No Image
                                    </div>
                                  )}
                               </div>
                               <div className="md:w-2/3">
                                   <strong className="text-gray-600 mb-2 block text-base">Opening Hours:</strong>
                                   {selectedLead.opening_hours ? (
                                      <div className="space-y-1 text-xs bg-gray-50 p-3 rounded border max-h-60 overflow-y-auto">
                                          {selectedLead.opening_hours.split(',').map(part => part.trim()).filter(Boolean).map((dayHours, index) => {
                                             const parts = dayHours.split(':');
                                             const day = parts[0];
                                             const hours = parts[1]?.replace(/[\[\]]/g, '').trim() || 'N/A'; // Remove brackets
                                             return (
                                                <div key={index} className="flex justify-between">
                                                   <span className="font-medium text-gray-700">{day}:</span>
                                                   <span className="text-gray-600 text-right">{hours}</span>
                                                </div>
                                             );
                                          })}
                                      </div>
                                   ) : (
                                      <p className="text-gray-500 italic">N/A</p>
                                   )}
                               </div>
                            </div>
                            <hr className="my-4"/>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                               <div><strong className="text-gray-600">Rating:</strong> {selectedLead.average_rating != null ? `${selectedLead.average_rating.toFixed(1)} ★` : 'N/A'}</div>
                               <div><strong className="text-gray-600">Distance:</strong> {selectedLead.distance_from_so16 != null ? `${selectedLead.distance_from_so16.toFixed(2)} km` : 'N/A'}</div>
                               <div><strong className="text-gray-600">Reviews:</strong> {selectedLead.review_count ?? 'N/A'}</div>
                               <div>
                                  <strong className="text-gray-600 mr-2">Status:</strong> 
                                  <span className={`px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full ${statusColorMap[selectedLead.status || 'default'] || statusColorMap.default}`}>
                                    {selectedLead.status ? selectedLead.status.replace('_', ' ') : 'N/A'}
                                  </span>
                               </div>
                            </div>
                            <div className="pt-4 border-t">
                              <div>
                                  <strong className="text-gray-600">Address:</strong> 
                                  {selectedLead.address ? (
                                      <a 
                                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(selectedLead.address)}`} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="text-primary-600 hover:underline ml-1"
                                        title="View on Google Maps"
                                       >
                                         {selectedLead.address}
                                      </a>
                                  ) : (
                                      <span className="ml-1">N/A</span>
                                  )}
                              </div>
                              <div className="mt-2">
                                 <strong className="text-gray-600">Email:</strong> 
                                 {selectedLead.email ? (
                                     selectedLead.email.split(',').map(e => e.trim()).filter(Boolean).map((individualEmail, index) => (
                                         <React.Fragment key={index}>
                                            {index > 0 && ", "} 
                                            <a href={`mailto:${individualEmail}`} className="text-primary-600 hover:underline ml-1">{individualEmail}</a>
                                         </React.Fragment>
                                     ))
                                 ) : (
                                     <span className="ml-1">N/A</span>
                                 )}
                              </div>
                            </div>
                            {selectedLead.categories && (
                                <div className="pt-4 border-t">
                                    <strong className="text-gray-600 mb-1 block">Categories:</strong>
                                    <div className="flex flex-wrap gap-2">
                                        {selectedLead.categories.split(',').map(cat => cat.trim()).filter(Boolean).map(c => (
                                            <span key={c} className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full text-xs">{c}</span>
                                        ))}
                                    </div>
                                </div>
                            )}
                            <div className="pt-4 border-t flex flex-wrap gap-x-4 gap-y-2 items-center">
                              <strong className="text-gray-600 mr-2">Links:</strong>
                              <a 
                                 href={`https://www.google.com/maps/dir/?api=1&origin=7+Grafton+Gardens,+SO16+5SQ&destination=${encodeURIComponent(selectedLead.address || `${selectedLead.latitude},${selectedLead.longitude}`)}`} 
                                 target="_blank" 
                                 rel="noopener noreferrer" 
                                 className="text-gray-700 hover:text-blue-500 flex items-center gap-1" 
                                 title="Get Directions"
                               >
                                 <FaMapMarkerAlt className="h-4 w-4"/> Directions
                               </a>
                              {selectedLead.google_knowledge_url && <a href={selectedLead.google_knowledge_url} target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-red-600 flex items-center gap-1" title="Google Info"><FaGoogle className="h-4 w-4"/> Google</a>}
                              {selectedLead.website && <a href={selectedLead.website} target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-blue-600 flex items-center gap-1" title={selectedLead.website}><FaGlobe className="h-4 w-4"/> Website</a>}
                              {selectedLead.facebook && <a href={selectedLead.facebook} target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-blue-800 flex items-center gap-1" title={selectedLead.facebook}><FaFacebook className="h-4 w-4"/> Facebook</a>}
                              {selectedLead.instagram && <a href={selectedLead.instagram} target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-pink-600 flex items-center gap-1" title={selectedLead.instagram}><FaInstagram className="h-4 w-4"/> Instagram</a>}
                            </div>
                            
                            {/* Add delete section */}
                            <div className="pt-6 mt-4 border-t">
                              <div className="flex justify-between items-center">
                                <div className="text-sm text-gray-600">
                                  <strong>Danger Zone</strong>
                                  <p className="text-xs text-gray-500 mt-1">Permanently delete this lead from the database.</p>
                                </div>
                                
                                {!showDeleteConfirm ? (
                                  <button 
                                    onClick={() => setShowDeleteConfirm(true)}
                                    className="px-4 py-2 bg-red-50 border border-red-200 text-red-600 text-sm rounded-md hover:bg-red-100"
                                  >
                                    Delete Lead
                                  </button>
                                ) : (
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm text-red-600 font-medium">Are you sure?</span>
                                    <button 
                                      onClick={() => setShowDeleteConfirm(false)}
                                      className="px-3 py-1.5 bg-gray-100 text-gray-700 text-sm rounded-md hover:bg-gray-200"
                                    >
                                      Cancel
                                    </button>
                                    <button 
                                      onClick={handleDeleteLead}
                                      disabled={isDeleting}
                                      className="px-3 py-1.5 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 disabled:opacity-50"
                                    >
                                      {isDeleting ? 'Deleting...' : 'Confirm Delete'}
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                        </div>
                     )}

                     {activeTab === 'manage' && (
                         <div className="space-y-6">
                            <div>
                                <label htmlFor="leadStatus" className="block text-sm font-medium text-gray-700 mb-1">Lead Status</label>
                                <div className="flex items-center gap-2">
                                    <select 
                                        id="leadStatus"
                                        value={currentLeadStatus}
                                        onChange={(e) => setCurrentLeadStatus(e.target.value)}
                                        className="block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 text-sm"
                                    >
                                        {LEAD_STATUSES.map(status => (
                                            <option key={status} value={status} className="capitalize">{status.replace('_', ' ')}</option>
                                        ))}
                                    </select>
                                    <button 
                                        onClick={handleStatusUpdate}
                                        disabled={isUpdatingStatus || currentLeadStatus === selectedLead.status}
                                        className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:opacity-50"
                                    >
                                        {isUpdatingStatus ? 'Updating...' : 'Update Status'}
                                    </button>
                                </div>
                             </div>

                             <div>
                                 <label htmlFor="leadNotesTextarea" className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                                 <textarea 
                                    id="leadNotesTextarea"
                                    rows={4}
                                    value={currentLeadNotes}
                                    onChange={(e) => setCurrentLeadNotes(e.target.value)}
                                    placeholder="Add notes about interactions, progress, etc..."
                                    className="block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 text-sm"
                                 ></textarea>
                                 <div className="text-right mt-2">
                                     <button 
                                        onClick={handleNotesSave}
                                        disabled={isSavingNotes}
                                        className="px-4 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 disabled:opacity-50"
                                      >
                                        {isSavingNotes ? 'Saving...' : 'Save Notes'}
                                     </button>
                                 </div>
                             </div>

                             <div className="border-t pt-4 space-x-3">
                                 <button 
                                    onClick={handleSendEmailClick}
                                    disabled={!selectedLead.email}
                                    className="px-4 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    Send Email
                                 </button>
                                     <button 
                                      onClick={() => alert('Set Follow-up Date - Not Implemented')}
                                      className="px-4 py-2 bg-gray-200 text-gray-700 text-sm rounded-md hover:bg-gray-300"
                                  >
                                    Set Follow-up
                                  </button>
                             </div>
                         </div>
                     )}
                 </div>
               </div>
             </div>
          </div>
      )}
    </>
  );
};

export default Dashboard;
